// sim/diagnostics.ts — COLONY BOTTLENECK DIAGNOSTICS: a PURE, OBSERVE-ONLY read-model
// that explains WHY a colony has stopped growing.
//
// THE PROBLEM it solves: a colony plateaus and neither the player (web HUD) nor a
// developer (CLI census) can see the single thing holding it back. The carrying-
// capacity layer (src/sim/capacity.ts) already exposes capacity + pressure + income;
// this layer READS those (plus the colony's own census + spawn clock) and distils
// them into ONE binding growth limiter, an expected growth rate, and a resource-flow
// snapshot.
//
// CRITICAL — this is NOT a tick System:
//   • It NEVER mutates state, draws RNG, or runs in the GameLoop.
//   • It is NOT folded into any state hash (compute it before/after a tick → the hash
//     is byte-identical). It is a VIEW over already-deterministic state, called on
//     demand (web per-frame, CLI census, tests).
//   • It is a pure FUNCTION over accessors — it accepts STRUCTURAL readers (the ctx
//     below), never the concrete classes, so tests can pass tiny stubs and the real
//     world systems satisfy it for free.
//   • Returning a fresh object per call is fine: it is a read-model view, not the
//     per-tick hot path. (Mirrors the render-only layers in DESIGN.md §19/§24.)
//
// DETERMINISM: same seed + same tick ⇒ identical ColonyDiagnostics (it reads only
// integer, already-deterministic state through the accessors; no RNG, no clock of its
// own). Integer arithmetic throughout.

import { CASTE } from '../types.ts';
import type { ColonyId, Caste } from '../types.ts';

// ---------------------------------------------------------------------------
// Classifications (Node 22 type-stripping: `as const` maps, NO enums).
// ---------------------------------------------------------------------------

/**
 * The single binding growth limiter for a colony. FIRST MATCH WINS down a fixed
 * cascade (see computeColonyDiagnostics), so a colony reports the ONE thing it must
 * fix to grow — not a soup of every pressure at once.
 */
export const BOTTLENECK = {
  /** Nothing is binding — the colony is (or could be) growing. */
  None: 0,
  /** Food supply is the limiter: too little stored / too weak an income to spawn. */
  Food: 1,
  /** A full larder pinned at carrying capacity — needs structural STORAGE to grow. */
  Storage: 2,
  /** Too few Workers to run the economy that feeds growth. */
  Worker: 3,
  /** No developing brood to raise (no headroom / nothing to convert). */
  Larvae: 4,
  /** Predator/threat pressure near the nest is suppressing the colony. */
  Defense: 5,
  /** At capacity with income but no room to RAISE capacity — must EXPAND (hive nodes). */
  Expansion: 6,
} as const;
export type Bottleneck = (typeof BOTTLENECK)[keyof typeof BOTTLENECK];

/** Human name per BOTTLENECK.* (index-aligned). */
export const BOTTLENECK_NAME = [
  'None', 'Food', 'Storage', 'Worker', 'Larvae', 'Defense', 'Expansion',
] as const;

/** Coarse growth trajectory — the headline "is it moving?" read. */
export const GROWTH_TREND = {
  /** Actively shrinking (e.g. under heavy threat). */
  Declining: 0,
  /** Pinned — a real bottleneck is holding it at its current size. */
  Stalled: 1,
  /** Holding steady at/near carrying capacity (healthy hold, not a defect). */
  Stable: 2,
  /** Has room and the means to grow. */
  Growing: 3,
} as const;
export type GrowthTrend = (typeof GROWTH_TREND)[keyof typeof GROWTH_TREND];

/** Human name per GROWTH_TREND.* (index-aligned). */
export const GROWTH_TREND_NAME = [
  'Declining', 'Stalled', 'Stable', 'Growing',
] as const;

// ---------------------------------------------------------------------------
// OPTIONAL windowed population-trend sampler — CALLER-OWNED ring buffer.
//
// The default trend is INFERRED from the bottleneck + threat (no time-series). A
// caller that wants a TRUE slope owns one PopulationSample ring per colony, pushes
// the live population into it each time it samples (its own cadence), and passes it
// into computeColonyDiagnostics. THE SAMPLER STATE LIVES IN THE CALLER — it is never
// folded into any sim hash, so diagnostics stays a pure, hash-neutral view. When no
// sample is passed, the trend falls back EXACTLY to the inferred value (byte-identical).
//
// Determinism: integer ring of pop readings; the slope is the integer delta between
// the oldest and newest reading in the window (first-vs-last). No RNG, no float.
// ---------------------------------------------------------------------------

/**
 * A tiny fixed-capacity ring of recent population readings for ONE colony, owned by
 * the caller. `data` is the backing store, `head` the next write slot, `len` the
 * count of valid readings (≤ capacity). All integer. Construct via newPopulationSample;
 * feed via pushPopulationSample; it is a plain data record (no methods, no hash).
 */
export interface PopulationSample {
  /** Backing ring; length is the window capacity. Holds the last `len` pop readings. */
  readonly data: Int32Array;
  /** Index of the next write (wraps modulo data.length). */
  head: number;
  /** Number of valid readings currently in the ring (saturates at data.length). */
  len: number;
}

/**
 * Allocate a population-sample ring with `window` slots (clamped ≥ 2 so a slope is
 * computable). CALLER-OWNED: build one per colony, keep it across frames, push the
 * live population each sample. Allocation happens once at construction — NOT in any
 * hot path. The ring carries no sim state and is never hashed.
 */
export function newPopulationSample(window: number): PopulationSample {
  const cap = window >= 2 ? Math.floor(window) : 2;
  return { data: new Int32Array(cap), head: 0, len: 0 };
}

/**
 * Push one population reading into the ring (oldest reading is overwritten once the
 * window is full). MUTATES the caller-owned ring only — it touches no sim state and
 * is not part of any hash. Allocation-free. Returns the same ring for chaining.
 */
export function pushPopulationSample(s: PopulationSample, population: number): PopulationSample {
  s.data[s.head] = population | 0;
  s.head = (s.head + 1) % s.data.length;
  if (s.len < s.data.length) s.len++;
  return s;
}

/**
 * Integer slope over the window: newest reading − oldest reading (the net change
 * across the samples currently held). 0 with fewer than two readings (no slope yet).
 * PURE — reads the ring, mutates nothing.
 */
export function populationSlope(s: PopulationSample): number {
  if (s.len < 2) return 0;
  const cap = s.data.length;
  // Newest is the slot just before `head`; oldest is `len` steps back from newest.
  const newestIdx = (s.head - 1 + cap) % cap;
  const oldestIdx = (s.head - s.len + cap) % cap;
  return (s.data[newestIdx] as number) - (s.data[oldestIdx] as number);
}

/**
 * Map a windowed population slope to a GROWTH_TREND. Strictly positive ⇒ Growing,
 * strictly negative ⇒ Declining, flat ⇒ Stable when at/over capacity (a healthy
 * hold) else Stalled (pinned below the cap). `atCapacity` lets a flat-but-full
 * colony read as the healthy "Stable hold" rather than "Stalled". PURE, integer.
 */
function trendFromSlope(slope: number, atCapacity: boolean): GrowthTrend {
  if (slope > 0) return GROWTH_TREND.Growing;
  if (slope < 0) return GROWTH_TREND.Declining;
  return atCapacity ? GROWTH_TREND.Stable : GROWTH_TREND.Stalled;
}

// ---------------------------------------------------------------------------
// Structural inputs — minimal reader interfaces so callers (web, CLI, tests) can
// pass stubs. The concrete world systems satisfy these structurally:
//   colony  ← ColonySystem        capacity ← CarryingCapacitySystem
//   pressure← EcosystemPressureSystem (optional)   ai ← ColonyAISystem (optional)
//   hive    ← HiveSystem (optional)
// We never import the concrete classes — only the shapes we read.
// ---------------------------------------------------------------------------

/** The per-colony economy + census reader (ColonySystem satisfies this). */
export interface DiagColonyReader {
  populationAt(cid: ColonyId): number;
  foodAt(cid: ColonyId): number;
  broodReadyAt(cid: ColonyId): number;
  casteCountAt(cid: ColonyId, caste: Caste): number;
  spawnCooldownAt(cid: ColonyId): number;
  isAlive(cid: ColonyId): boolean;
}

/** The carrying-capacity reader (CarryingCapacitySystem satisfies this). */
export interface DiagCapacityReader {
  capacityOf(cid: ColonyId): number;
  /** Population pressure in permille == capacity UTILISATION. */
  pressureOf(cid: ColonyId): number;
  cooldownScaleOf(cid: ColonyId): number;
  incomeOf(cid: ColonyId): number;
}

/** OPTIONAL wildlife→colony pressure reader (EcosystemPressureSystem satisfies it). */
export interface DiagPressureReader {
  predationAt(cid: ColonyId): number;
  aerialAt(cid: ColonyId): number;
}

/** OPTIONAL autonomy reader for founded hive nodes (ColonyAISystem satisfies it). */
export interface DiagAiReader {
  hiveCountAt(cid: ColonyId): number;
}

/**
 * OPTIONAL physical-hive reader (HiveSystem satisfies it). `hasChamber` is
 * informational; `storageChamberCountAt` (optional) feeds the Storage-vs-Food
 * sharpening at capacity. HiveSystem can satisfy the count via a thin adapter that
 * tallies `chamberTypeAt(cid,k) === CHAMBER.Storage` over `chamberCountOf(cid)`.
 */
export interface DiagHiveReader {
  hasChamber(cid: ColonyId, type: number): boolean;
  /** Count of built Storage chambers the colony holds (0 if none). OPTIONAL. */
  storageChamberCountAt?(cid: ColonyId): number;
}

/**
 * The diagnostics context — a bundle of the readers above. Concrete world systems
 * satisfy it structurally, so the web passes `world.colonies`, `world.capacity`, and
 * (when present) `world.pressure` / `world.ai` / `world.hive`. The scalar spawn knobs
 * come from the colony config.
 */
export interface DiagnosticsCtx {
  readonly colony: DiagColonyReader;
  readonly capacity: DiagCapacityReader;
  readonly pressure?: DiagPressureReader | undefined;
  readonly ai?: DiagAiReader | undefined;
  readonly hive?: DiagHiveReader | undefined;
  /** Food a colony must hold to spawn one organism (ColonyConfig.spawnCost). */
  readonly spawnCost: number;
  /** Base ticks between spawns (ColonyConfig.spawnCooldown). */
  readonly spawnCooldown: number;
  /**
   * OPTIONAL direct override of the colony's built Storage-chamber count, sharpening
   * the Storage-vs-Food call at capacity (see the cascade). Takes precedence over
   * `hive.storageChamberCountAt`. ABSENT (undefined) ⇒ the cascade ignores chamber
   * count entirely and is byte-identical to the chamber-unaware behaviour. Provide a
   * fixed map/array read for determinism; a fractional value is floored.
   */
  readonly storageChambers?: number | undefined;
}

/**
 * Resolve the colony's Storage-chamber count from the ctx, or `undefined` when no
 * storage signal was supplied. The scalar `storageChambers` override wins; otherwise
 * the optional `hive.storageChamberCountAt` reader is consulted. PURE: a read-only
 * lookup, floored to a non-negative integer. `undefined` (not 0) is the "absent"
 * sentinel so the cascade can stay byte-identical when no storage signal exists —
 * 0 chambers is a real, distinct signal (an at-cap colony that has built NO storage).
 */
function resolveStorageChambers(ctx: DiagnosticsCtx, cid: ColonyId): number | undefined {
  if (ctx.storageChambers !== undefined) {
    const n = Math.floor(ctx.storageChambers);
    return n > 0 ? n : 0;
  }
  const reader = ctx.hive?.storageChamberCountAt;
  if (reader !== undefined) {
    const n = Math.floor(reader.call(ctx.hive, cid));
    return n > 0 ? n : 0;
  }
  return undefined;
}

// ---------------------------------------------------------------------------
// Tuning.
// ---------------------------------------------------------------------------

export interface DiagnosticsConfig {
  /** Threat (predation+aerial) at/above which Defense is the binding limiter. */
  readonly threatHi: number;
  /** Capacity utilisation (permille) at/above which the colony is capacity-bound. */
  readonly atCapPermille: number;
  /** Food income at/below which the supply line counts as "starved". */
  readonly incomeLo: number;
  /** Hive-node count below which a capacity-bound colony should EXPAND first. */
  readonly expandMinNodes: number;
  /** Worker ratio (permille of population) below which Workers are the limiter. */
  readonly workerLoPermille: number;
}

/**
 * Default tuning. One-line rationale each:
 *  • threatHi 6        — aligns with EcosystemPressureSystem's squelch thresholds
 *                        (forageSquelch/aerialSquelch ≈6): the point where pressure
 *                        already pulls foragers in / suppresses flyers, i.e. growth
 *                        is genuinely being choked, not merely nibbled.
 *  • atCapPermille 880 — just above capacity nearBand (850, src/sim/capacity.ts): the
 *                        colony has entered the "Stable hold" band, so further growth
 *                        means RAISING capacity, not spawning harder.
 *  • incomeLo 1        — income is sampled per cadence; ≤1 unit/cadence is effectively
 *                        no supply line (a single straggler), so the larder cannot refill.
 *  • expandMinNodes 2  — below a couple of founded hive nodes a capacity-bound colony's
 *                        cheapest lever is EXPANSION (each node adds hiveNodeWeight cap),
 *                        not stockpiling.
 *  • workerLoPermille 350 — under ~35% Workers the gathering economy is too thin to
 *                        sustain growth; the default SPAWN_CYCLE is ~50% Worker, so 35%
 *                        flags a genuinely worker-starved mix without false-positiving
 *                        a healthy colony.
 * Tuned to read sensibly across seeds in the CLI census + web HUD.
 */
export function defaultDiagnosticsConfig(): DiagnosticsConfig {
  return {
    threatHi: 6,
    atCapPermille: 880,
    incomeLo: 1,
    expandMinNodes: 2,
    workerLoPermille: 350,
  };
}

// ---------------------------------------------------------------------------
// The read-model. Players + devs read THIS.
// ---------------------------------------------------------------------------

export interface ColonyDiagnostics {
  /** Colony id this snapshot describes. */
  readonly cid: ColonyId;
  /** The single binding growth limiter (BOTTLENECK.*). */
  readonly bottleneck: Bottleneck;
  /** Human name of `bottleneck` (BOTTLENECK_NAME). */
  readonly bottleneckName: string;
  /**
   * ALL active growth limiters, most-binding first — `limiters[0] === bottleneck`
   * always. Additive read: the primary `bottleneck` classification is unchanged; this
   * exposes any OTHER limits that are ALSO binding right now (e.g. Food AND Defense),
   * which the single-bottleneck cascade collapses away. Never contains BOTTLENECK.None
   * and never duplicates. A fresh array per call (a view, not the hot path).
   */
  readonly limiters: readonly Bottleneck[];
  /** Human names of `limiters` (index-aligned with it). */
  readonly limiterNames: readonly string[];
  /** `limiters` without the primary — the CO-limiters only (empty when only one binds). */
  readonly secondaryBottlenecks: readonly Bottleneck[];
  /** Coarse trajectory (GROWTH_TREND.*). */
  readonly trend: GrowthTrend;
  /** Human name of `trend` (GROWTH_TREND_NAME). */
  readonly trendName: string;
  /** Current live population. */
  readonly population: number;
  /** Current dynamic carrying capacity. */
  readonly capacity: number;
  /** Capacity utilisation in permille (== pressure). */
  readonly capacityUtilPermille: number;
  /** Expected gross spawns per 100 ticks (≈0 when bottlenecked — that's the point). */
  readonly expectedGrowthPer100: number;
  /** Food currently stored. */
  readonly foodStore: number;
  /** Food income per cadence (the supply line). */
  readonly foodIncome: number;
  /** Estimated food consumed per cadence (spawn draw + any over-capacity tax). */
  readonly foodConsumeEst: number;
  /** Net food flow per cadence (income − consume). */
  readonly foodNet: number;
  /** Worker ratio in permille of population. */
  readonly workerRatioPermille: number;
  /** Developing brood the colony could raise right now. */
  readonly broodReady: number;
  /** Sensed threat level (predation + aerial) near the nest; 0 with no pressure layer. */
  readonly threatLevel: number;
  /** Founded hive nodes; 0 with no AI layer. */
  readonly hiveNodes: number;
}

/** A dead/empty colony's diagnostics — bottleneck None, trend Stalled, zeros. */
function emptyDiagnostics(cid: ColonyId): ColonyDiagnostics {
  return {
    cid,
    bottleneck: BOTTLENECK.None,
    bottleneckName: BOTTLENECK_NAME[BOTTLENECK.None],
    limiters: [],
    limiterNames: [],
    secondaryBottlenecks: [],
    trend: GROWTH_TREND.Stalled,
    trendName: GROWTH_TREND_NAME[GROWTH_TREND.Stalled],
    population: 0,
    capacity: 0,
    capacityUtilPermille: 0,
    expectedGrowthPer100: 0,
    foodStore: 0,
    foodIncome: 0,
    foodConsumeEst: 0,
    foodNet: 0,
    workerRatioPermille: 0,
    broodReady: 0,
    threatLevel: 0,
    hiveNodes: 0,
  };
}

/**
 * Build the ordered ACTIVE-LIMITER list: the primary `primary` first, then every
 * OTHER limit whose own predicate also holds, in BOTTLENECK priority order. PURE,
 * integer; allocates one small array (a view, not the hot path).
 *
 * Each limit re-probes the SAME condition the cascade short-circuits on, but
 * independently of priority, so a colony binding on two axes at once (e.g. Food AND
 * Defense, or at-cap Expansion AND a thin workforce) reports both. The predicates are
 * regime-aware so they don't fire spuriously:
 *   • Defense   — threat ≥ threatHi (existential; can co-bind with anything).
 *   • Food      — supply/store is binding: at cap, income ≤ incomeLo OR (storage known
 *                 == 0); below cap, food < spawnCost OR income ≤ incomeLo.
 *   • Storage   — at cap, income present, nodes sufficient, storage unknown-or-≥1.
 *   • Worker    — workforce thin (workerRatio < workerLoPermille) AND below capacity
 *                 (a thin gathering crew only throttles active growth, not a held cap).
 *   • Larvae    — below cap, supplied (food ≥ spawnCost, income > incomeLo), no brood.
 *   • Expansion — at cap, income present, too few hive nodes.
 * `primary` is ALWAYS included (it is, by construction, one of these), de-duplicated
 * and floated to the front. None is never listed.
 */
function collectLimiters(
  primary: Bottleneck,
  cfg: DiagnosticsConfig,
  threat: number,
  util: number,
  income: number,
  hiveNodes: number,
  storageChambers: number | undefined,
  food: number,
  spawnCost: number,
  brood: number,
  workerRatio: number,
): Bottleneck[] {
  if (primary === BOTTLENECK.None) return [];
  const atCap = util >= cfg.atCapPermille;
  const supplyStarved = food < spawnCost || income <= cfg.incomeLo;
  const out: Bottleneck[] = [];

  // Defense first (highest cascade priority), then the rest in BOTTLENECK order.
  if (threat >= cfg.threatHi) out.push(BOTTLENECK.Defense);

  const foodBinds = atCap
    ? (income <= cfg.incomeLo || storageChambers === 0)
    : supplyStarved;
  if (foodBinds) out.push(BOTTLENECK.Food);

  const storageBinds = atCap
    && income > cfg.incomeLo
    && hiveNodes >= cfg.expandMinNodes
    && storageChambers !== 0; // unknown (undefined) or ≥1 — a full larder needing rooms
  if (storageBinds) out.push(BOTTLENECK.Storage);

  // A thin workforce only throttles ACTIVE growth (below the cap); at a held cap the
  // crew size isn't the lever, so we don't co-list Worker there.
  if (!atCap && workerRatio < cfg.workerLoPermille) out.push(BOTTLENECK.Worker);

  // Larvae: below cap, actually supplied, but nothing to raise.
  if (!atCap && !supplyStarved && brood <= 0) out.push(BOTTLENECK.Larvae);

  // Expansion: at cap with income but too few founded nodes.
  if (atCap && income > cfg.incomeLo && hiveNodes < cfg.expandMinNodes) out.push(BOTTLENECK.Expansion);

  // Guarantee the primary is present and FIRST (de-dup, then unshift if needed).
  let i = out.indexOf(primary);
  if (i < 0) {
    out.unshift(primary);
  } else if (i > 0) {
    out.splice(i, 1);
    out.unshift(primary);
  }
  return out;
}

/**
 * Diagnose colony `cid`: the single binding growth limiter, its trajectory, the
 * expected growth rate, and the resource-flow snapshot. PURE — reads only the ctx
 * accessors (+ the optional caller-owned `sample`); no mutation of sim state, no RNG,
 * no clock. The optional `sample` ring is the caller's; pushing into it is the
 * caller's job and never touches the sim — passing it here only READS its slope.
 *
 * THE BOTTLENECK CASCADE (deterministic, first match wins):
 *   1. threat ≥ threatHi                       → Defense   (predators choke growth)
 *   2. util ≥ atCapPermille (capacity-bound; to grow you must RAISE capacity):
 *        income ≤ incomeLo                     → Food      (no supply to lift cap)
 *        else hiveNodes < expandMinNodes       → Expansion (cheapest cap lever)
 *        else if storageChambers KNOWN and == 0 → Food     (full larder, no store to
 *                                                            build into — sharpened)
 *        else                                  → Storage   (a full larder, needs rooms)
 *   3. else (below capacity but not spawning — find the spawn block):
 *        food < spawnCost OR income ≤ incomeLo → Worker if worker-starved else Food
 *        else brood ≤ 0                        → Larvae
 *        else workerRatio < workerLoPermille   → Worker
 *        else                                  → None      (it can grow)
 *
 * STORAGE SHARPENING (opt-in): the Storage-vs-Food call at capacity is heuristic
 * because colony food is uncapped. When the ctx supplies a storage-chamber count
 * (`storageChambers` scalar or `hive.storageChamberCountAt`), an at-cap colony with
 * income that has built NO storage reads as Food (it needs storage built before
 * "Storage Limited" is honest); ≥1 storage chamber confirms the Storage call. When NO
 * storage signal is supplied, the cascade is byte-identical to the chamber-unaware path.
 *
 * TREND (opt-in true slope): without `sample` the trend is INFERRED from the
 * bottleneck + threat (the historical behaviour). With a caller-owned `sample` ring
 * holding ≥2 readings, the trend is the windowed population slope (newest − oldest);
 * the inferred trend is the fallback when the ring has <2 readings — so absent or
 * cold, the result is byte-identical to today.
 */
export function computeColonyDiagnostics(
  ctx: DiagnosticsCtx,
  cid: ColonyId,
  cfg: DiagnosticsConfig = defaultDiagnosticsConfig(),
  sample?: PopulationSample | undefined,
): ColonyDiagnostics {
  const { colony, capacity } = ctx;

  // Dead / empty colony — nothing to diagnose.
  if (!colony.isAlive(cid)) return emptyDiagnostics(cid);
  const pop = colony.populationAt(cid);
  if (pop === 0) return emptyDiagnostics(cid);

  // --- Gather (all integer reads through the accessors). ---
  const cap = capacity.capacityOf(cid);
  const util = capacity.pressureOf(cid);              // permille == capacity utilisation
  const food = colony.foodAt(cid);
  const income = capacity.incomeOf(cid);
  const workers = colony.casteCountAt(cid, CASTE.Worker);
  const workerRatio = pop > 0 ? Math.floor((workers * 1000) / pop) : 0;
  const brood = colony.broodReadyAt(cid);
  const threat = ctx.pressure
    ? ctx.pressure.predationAt(cid) + ctx.pressure.aerialAt(cid)
    : 0;
  const hiveNodes = ctx.ai ? ctx.ai.hiveCountAt(cid) : 0;
  // OPTIONAL storage-chamber signal — `undefined` means "no signal supplied", which
  // keeps the cascade byte-identical to the chamber-unaware behaviour below.
  const storageChambers = resolveStorageChambers(ctx, cid);

  // --- Bottleneck cascade (first match wins). ---
  let bottleneck: Bottleneck;
  if (threat >= cfg.threatHi) {
    bottleneck = BOTTLENECK.Defense;
  } else if (util >= cfg.atCapPermille) {
    // At/over carrying capacity ⇒ capacity-bound: to grow you must RAISE capacity.
    if (income <= cfg.incomeLo) {
      bottleneck = BOTTLENECK.Food;            // no supply line to lift the cap
    } else if (hiveNodes < cfg.expandMinNodes) {
      bottleneck = BOTTLENECK.Expansion;       // cheapest lever: found a hive node
    } else if (storageChambers === 0) {
      // SHARPENED (opt-in): a full larder at capacity with income + nodes but ZERO
      // built Storage chambers can't be "Storage Limited" yet — there is no store to
      // convert the larder into capacity. The honest lever is the supply/store gap →
      // Food. Only fires when the storage count was actually supplied (else undefined).
      bottleneck = BOTTLENECK.Food;
    } else {
      bottleneck = BOTTLENECK.Storage;         // a full larder pinned at the cap
    }
  } else {
    // Below capacity but not spawning — find what blocks the spawn loop.
    if (food < ctx.spawnCost || income <= cfg.incomeLo) {
      bottleneck = workerRatio < cfg.workerLoPermille ? BOTTLENECK.Worker : BOTTLENECK.Food;
    } else if (brood <= 0) {
      bottleneck = BOTTLENECK.Larvae;
    } else if (workerRatio < cfg.workerLoPermille) {
      bottleneck = BOTTLENECK.Worker;
    } else {
      bottleneck = BOTTLENECK.None;
    }
  }

  // --- Limiters (the primary PLUS any co-binding limits, most-binding first). ---
  // ADDITIVE ONLY: the primary `bottleneck` above is untouched. We re-probe each
  // limit's OWN predicate independently (the same conditions the cascade short-circuits
  // on) and list every one that holds, in cascade priority order, with the primary
  // first. This surfaces co-limits (e.g. Food AND Defense) that the single-bottleneck
  // cascade collapses. None is never listed. A fresh array per call (a view).
  const limiters = collectLimiters(
    bottleneck, cfg, threat, util, income, hiveNodes, storageChambers,
    food, ctx.spawnCost, brood, workerRatio,
  );

  // --- Trend. ---
  // OPT-IN true slope: a caller-owned `sample` ring holding ≥2 readings yields a real
  // population slope, which drives the trend (Growing/Declining/Stable/Stalled). With
  // NO ring (or a ring with <2 readings) we fall back EXACTLY to the inferred trend:
  //   None ⇒ Growing. A besieged colony (Defense) is actively pushed down ⇒ Declining,
  //   taking precedence over the at-capacity "Stable hold" (the parenthetical in the
  //   spec's trend rule: "Defense(high threat)→Declining"). Otherwise an at/over-cap
  //   colony is at a healthy Stable hold; any other bottleneck is Stalled.
  const atCapacity = util >= cfg.atCapPermille;
  let trend: GrowthTrend;
  if (sample !== undefined && sample.len >= 2) {
    trend = trendFromSlope(populationSlope(sample), atCapacity);
  } else if (bottleneck === BOTTLENECK.None) {
    trend = GROWTH_TREND.Growing;
  } else if (bottleneck === BOTTLENECK.Defense) {
    trend = GROWTH_TREND.Declining;
  } else if (atCapacity) {
    trend = GROWTH_TREND.Stable;
  } else {
    trend = GROWTH_TREND.Stalled;
  }

  // --- Expected gross growth per 100 ticks. ---
  // Effective spawn cooldown in ticks (capacity may shorten it via cooldownScaleOf).
  const effCd = Math.max(1, Math.floor((ctx.spawnCooldown * capacity.cooldownScaleOf(cid)) / 1000));
  // A non-None bottleneck ⇒ ~0 growth — that IS the diagnostic. When unblocked, gross
  // spawn potential is one spawn per effCd ticks → 100/effCd per 100 ticks.
  const expectedGrowthPer100 = bottleneck === BOTTLENECK.None ? Math.round(100 / effCd) : 0;

  // --- Resource flow (per cadence, integer + deterministic). ---
  // The spawn draw: a growing colony converts spawnCost food into one brood per the
  // capacity income-window (one brood per window is the steady draw of a healthy,
  // paced colony). A bottlenecked colony is, by definition, NOT spawning → no draw.
  // When truly OVER capacity (util > 1000‰) the capacity layer wastes food every
  // cadence (the efficiency tax) and the colony cannot grow regardless — the honest
  // symptom is foodNet ≤ 0, so we treat the whole income as drained. Integer-only.
  const consume = bottleneck === BOTTLENECK.None
    ? ctx.spawnCost
    : (util > 1000 ? (income > 0 ? income : 0) : 0);

  const foodNet = income - consume;

  return {
    cid,
    bottleneck,
    bottleneckName: BOTTLENECK_NAME[bottleneck],
    limiters,
    limiterNames: limiters.map((b) => BOTTLENECK_NAME[b]),
    secondaryBottlenecks: limiters.slice(1),
    trend,
    trendName: GROWTH_TREND_NAME[trend],
    population: pop,
    capacity: cap,
    capacityUtilPermille: util,
    expectedGrowthPer100,
    foodStore: food,
    foodIncome: income,
    foodConsumeEst: consume,
    foodNet,
    workerRatioPermille: workerRatio,
    broodReady: brood,
    threatLevel: threat,
    hiveNodes,
  };
}

/**
 * One-line dev/CLI summary of a diagnostics snapshot:
 *   c{cid} {BOTTLENECK} {util%} {pop}/{cap} grow±{N} {in}/{store}/{out}
 * e.g. `c0 Expansion 92% 55/60 grow+0 4/110/0`.
 */
export function diagnosticsCensusLine(d: ColonyDiagnostics): string {
  const util = Math.round(d.capacityUtilPermille / 10);
  const grow = d.expectedGrowthPer100 >= 0 ? `+${d.expectedGrowthPer100}` : `${d.expectedGrowthPer100}`;
  return (
    `c${d.cid} ${d.bottleneckName} ${util}% ${d.population}/${d.capacity}` +
    ` grow${grow} ${d.foodIncome}/${d.foodStore}/${d.foodConsumeEst}`
  );
}
